import React, { useEffect, useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { useThree, useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import * as THREE from 'three';
import { IS_MOBILE, assetV, SLICE_DISTRICT } from '../config';
import { useWorldTime } from '../worldTime';
import { freeGltf } from '../utils/gltfMemory';
import { ktx2Extend } from '../utils/ktx2';
import { sky } from '../render/skyState';
import { warmAndTrack } from '../state/bootProgress';

// CitySkyline — an "endless city" backdrop: copies of a low-poly night-skyline
// chunk arranged in a ring around the world, facing inward, far out past the
// playable area. Night-lit window textures make it glow at dusk. Combined with
// the walk boundary, you never reach the ring, so the city reads as infinite.

// Phones load the mobile variant (0.5MB vs 9.5MB); desktop keeps the original.
// KTX2 build of the full skyline — see CityModel for the reasoning. 9.5MB →
// 2.1MB, and its 2048 sheets stop costing ~106MB of VRAM. Geometry and the one
// glow material are unchanged; `skyline.glb` is untouched for a one-line revert.
const MODEL_URL = assetV(IS_MOBILE ? '/models/skyline_mobile.glb' : '/models/skyline_desktop.glb');
const RING_RADIUS = 300;   // legacy fallback radius, used until bounds arrive
// How far outside the model the towers stand. Comfortably inside CityBox's
// ?boxpad so there is still sky visible between them and the wall — with no gap
// they read as painted onto the backdrop, which is the failure this layering
// exists to avoid.
const INSET = 8;   // gap between the map edge and the nearest tower face
const TARGET_HEIGHT = 95;  // scaled building height at that distance
const FACING = Math.PI;    // flip by ±Math.PI if the buildings face outward
const SINK_FRAC = 0.025;    // sink this fraction of the (scaled) height below ground so no base floats

// ── The OUTER ring, standing on the perimeter of the ground disc ────────────
//
// One row of towers cannot close a horizon. The inner ring hugs the map, so
// looking down a long avenue you are sighting through the GAPS BETWEEN its
// pieces — and past them there is nothing but sky. Every fix that stays inside
// one ring is a trade: pack it tighter and it reads as a wall, spread it and the
// holes come back.
//
// A second ring behind it is the cheap answer, and cheap literally: the chunk is
// 6,076 triangles in ONE draw call, and three.js clones share both geometry and
// material — so the whole outer ring costs about six draws and no new VRAM.
//
// It goes on the GROUND DISC'S PERIMETER (CityBox), which is where the eye
// already reads the horizon, and it is a CIRCLE because the disc is. The inner
// ring is a rectangle because it wraps a rectangular map; out here nothing is
// being wrapped, so a circle keeps every piece the same distance away and the
// backdrop reads the same in every direction.
//
// SIZED TO MATCH APPARENT HEIGHT, not real height. Twice as far away needs to be
// twice as tall to subtend the same angle, and matching the angle is exactly
// what makes it a gap-filler: it is invisible behind the inner ring and fills
// the sightlines that get past it, rather than becoming a second silhouette.
const RING2 = typeof window === 'undefined' || new URLSearchParams(window.location.search).get('ring2') !== '0';
// Where the perimeter is. MUST track CityBox's `pad` — that plus the city's own
// half-diagonal is the disc it draws. Read from the same query param so tuning
// ?boxpad moves the towers with the ground they stand on, instead of leaving
// them behind in mid-air.
const BOX_PAD_DEFAULT = 120;

// Tune live: ?ring2=0 (off) · ?ring2r (× the disc radius) · ?ring2h (× the
// apparent-height match — above 1 the outer towers rise over the inner ones).
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

// Module scope so the "no bounds yet" branch returns the SAME object every
// render — a fresh literal would be a new identity for anything downstream.
const NO_RINGS = { inner: [], outer: [], innerReach: 0, note: '' };

// `ground`: draw the terrain disc between the model city and the ring. Off when
// SeaVista is providing the surface instead (see below).
export default function CitySkyline({ ground = true, tint: tintOn = true }) {
  // The model's own footprint — the skyline wraps THAT, not a fixed radius.
  const bounds = useWorldStore((s) => s.cityBounds);
  const setSkylineOuter = useWorldStore((s) => s.setSkylineOuter);
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(MODEL_URL, true, true, ktx2);
  // Mobile: dump this 10MB backdrop's GPU memory on unmount (entering a splat
  // interior) so the splat fits; it reloads on return. Desktop keeps it cached.
  useEffect(() => {
    if (!IS_MOBILE) return undefined;
    return () => freeGltf(scene, MODEL_URL);
  }, [scene]);

  const tod = useWorldTime(SLICE_DISTRICT.timezone);

  // The ring sits past the scene fog's far plane — turn fog off on its materials
  // so it stays visible as the backdrop instead of fading out. Stash each
  // material's original colour so the atmosphere tint below can be re-applied
  // from the ORIGINAL each tick (never compounding).
  useMemo(() => {
    scene.traverse((o) => {
      if (!o.material) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        m.fog = false;
        if (m.color && !m.userData.baseColor) m.userData.baseColor = m.color.clone();
      });
    });
  }, [scene]);

  // ATMOSPHERE MATCH: the model city is lit by the world's time-of-day rig, so it
  // goes golden at dusk — but this backdrop has fog off and isn't lit, so it
  // stayed cold/dark and the fly-in read as two different worlds. Tint the ring
  // toward the current horizon colour (strongest by day, released at night when
  // the lit-window textures should carry it).
  useEffect(() => {
    const tint = new THREE.Color(tod?.horizon || '#ffd27d');
    // Under the new look the post pass's haze does this job for real — per pixel,
    // by distance and height — so a second, flat tint on top would double it.
    const amt = tintOn ? 0.55 * (1 - (tod?.night ?? 0)) : 0;
    scene.traverse((o) => {
      if (!o.material) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        if (!m.color || !m.userData.baseColor) return;
        m.color.copy(m.userData.baseColor).lerp(tint, amt);
        m.needsUpdate = true;
      });
    });
  }, [scene, tod?.horizon, tod?.night, tintOn]);

  // WINDOWS FOLLOW THE HOUR. The skyline's one material has its lit-window sheet
  // as an always-on emissive, so the backdrop glowed like midnight at noon. Under
  // the new look (tint off) the glow rides the shared sky: a faint few offices by
  // day, the whole wall of windows after dark.
  const glowMats = useMemo(() => {
    const out = [];
    scene.traverse((o) => {
      (Array.isArray(o.material) ? o.material : o.material ? [o.material] : []).forEach((m) => {
        if (m.emissiveMap && !out.some((x) => x.m === m)) out.push({ m, base: m.emissiveIntensity ?? 1 });
      });
    });
    return out;
  }, [scene]);
  useEffect(() => () => glowMats.forEach(({ m, base }) => { m.emissiveIntensity = base; }), [glowMats]);
  useFrame(() => {
    if (tintOn) return;
    const k = 0.1 + 1.5 * sky.night;
    for (const g of glowMats) g.m.emissiveIntensity = g.base * k;
  });

  // The filler ground under the ring: was a hardcoded near-black disc, which read
  // as VOID around the sunlit city. Now it takes the atmosphere's fog colour
  // (slightly darker than the sky so the city still reads on top of it).
  const groundColor = useMemo(() => {
    const c = new THREE.Color(tod?.fog || '#0c0c12');
    return c.multiplyScalar(0.7);
  }, [tod?.fog]);

  const items = useMemo(() => {
    if (!bounds) return NO_RINGS;   // nothing to wrap until the model reports its footprint
    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3(); box.getSize(size);
    const s = TARGET_HEIGHT / (size.y || 1);
    const pieceW = Math.max(1, (size.x || 1) * s);
    // Sink so every building base is below the ground plane (which occludes
    // them) — hides the model's uneven/floating bases.
    const groundY = -box.min.y * s - (size.y * s * SINK_FRAC);
    const step = pieceW * 0.85;   // slight overlap, no gaps

    // A RECTANGLE around the model, not a circle at a fixed radius.
    //
    // These now stand in the GAP between the city and CityBox's sky walls, and
    // that gap is rectangular because the model is. A circle inscribed in it
    // either leaves the long ends uncovered or pushes the towers out through
    // the short walls. Hugging the footprint also puts them CLOSE — which is
    // the point: near geometry against a far sky is what parallaxes.
    // CLOSE TO THE MODEL, deliberately. Packing them in tight is what gives the
    // density and covers the empty ground between city and backdrop — pushing
    // them outward to guarantee clearance thinned them out and exposed it again.
    //
    // Every rotation here is a multiple of 90°, so a chunk's world footprint is
    // just its local one with x/z swapped on the side runs — no general OBB
    // needed to test whether one has landed somewhere it shouldn't.
    // PLACE BY THE INNER FACE.
    //
    // A chunk is 227 x 200 world units at this scale and its geometry sits well
    // off its own origin (x −27, z +20) — the GLB's node hierarchy carries a
    // large transform, so where a chunk is PLACED and where it STANDS are
    // different points. Positioning by origin at a modest inset put the north
    // row's geometry from z −186 to +14, i.e. straight across the middle of a
    // map that is only 100 deep. That is the four buildings on the map, and no
    // amount of nudging the inset fixes it: the pieces are simply bigger than
    // the gap they were being asked to sit in.
    //
    // So solve for the position that puts each chunk's INNER EDGE exactly INSET
    // outside the map, and let its bulk fall away outward. Closest possible
    // packing without covering anything — the density stays, the overlap goes.
    const ctr = new THREE.Vector3(); box.getCenter(ctr);
    const offX = ctr.x * s, offZ = ctr.z * s;
    const hxW = (size.x * s) / 2, hzW = (size.z * s) / 2;

    // Rotations are all multiples of 90°, so the rotated offset and extents are
    // exact without a general OBB.
    const solve = (rotY, outward, axis) => {
      const cos = Math.round(Math.cos(rotY)), sin = Math.round(Math.sin(rotY));
      const oX = offX * cos + offZ * sin;
      const oZ = -offX * sin + offZ * cos;
      const sideways = Math.abs(sin) > 0.5;
      const half = axis === 'x' ? (sideways ? hzW : hxW) : (sideways ? hxW : hzW);
      const off = axis === 'x' ? oX : oZ;
      const edge = axis === 'x' ? bounds.max[0] : bounds.max[1];
      // outward = +1 pushes to the positive side, −1 to the negative.
      return outward * (edge + INSET) - off + outward * half;
    };

    const zN = solve(FACING, -1, 'z');
    const zS = solve(FACING + Math.PI, 1, 'z');
    const xW = solve(FACING + Math.PI / 2, -1, 'x');
    const xE = solve(FACING - Math.PI / 2, 1, 'x');
    // Runs span far enough that the corners still meet.
    const spanX = bounds.max[0] + INSET + hxW, spanZ = bounds.max[1] + INSET + hxW;

    const out = [];
    const run = (from, to, fixed, axis, rotY) => {
      const n = Math.max(2, Math.ceil(Math.abs(to - from) / step));
      for (let i = 0; i <= n; i++) {
        const t = from + ((to - from) * i) / n;
        const pos = axis === 'x' ? [t, groundY, fixed] : [fixed, groundY, t];
        out.push({ obj: scene.clone(true), pos, rotY, scale: s });
      }
    };
    // Each side faces INWARD at the city.
    run(-spanX, spanX, zN, 'x', FACING);
    run(-spanX, spanX, zS, 'x', FACING + Math.PI);
    run(-spanZ, spanZ, xW, 'z', FACING + Math.PI / 2);
    run(-spanZ, spanZ, xE, 'z', FACING - Math.PI / 2);

    // How far the inner ring reaches, measured off the placements themselves —
    // needed here (not just for the shell) because the outer ring is sized to
    // clear it.
    const innerReach = out.reduce((m, it) => Math.max(m, Math.hypot(it.pos[0], it.pos[2])), 0);

    // ── OUTER RING ──────────────────────────────────────────────────────────
    const outer = [];
    let outerR = 0;
    let log2 = 'off';
    if (RING2) {
      // The disc CityBox draws, from its own formula: the city's half-diagonal
      // plus the pad. Deliberately NOT read back from the shell's final radius —
      // that radius is itself sized to contain the skyline, so placing towers on
      // it would push it out, which would move the towers, which would push it
      // out again. Deriving from the city alone is what makes this converge.
      const hx = (bounds.max[0] - bounds.min[0]) / 2;
      const hz = (bounds.max[1] - bounds.min[1]) / 2;
      const discR = Math.hypot(hx, hz) + qp('boxpad', BOX_PAD_DEFAULT);
      const R2 = discR * qp('ring2r', 1);

      // Match apparent height against the inner ring's FARTHEST side. The map is
      // not square, so its long sides and its short ones stand at quite different
      // distances — there is no single distance to match, and the only choice is
      // which way to be wrong. Matching the far side yields the smaller tower,
      // which fills gaps without becoming the skyline; matching the near side
      // would put the backdrop over the top of the ring it hides behind.
      const R1 = Math.max(bounds.max[0], bounds.max[1]) + INSET;
      const matched = s * (R2 / Math.max(1, R1));

      // KEEP THE TOPS UNDER THE SKY. The shell is a sphere, so its ceiling drops
      // as you go out — at the perimeter there is far less headroom than there is
      // overhead. Predict the radius the shell will settle at (its own formula
      // again) and solve for the height that still fits under the dome at R2.
      const shellR = Math.max(discR, Math.max(innerReach, R2) * 1.25);
      const headroom = 0.9 * Math.sqrt(Math.max(0, shellR * shellR - R2 * R2));

      // ?ring2h APPLIES LAST, on purpose. The two rules above both push the same
      // way — the apparent-height match wants the outer ring to hide exactly
      // behind the inner one, and the headroom cap trims it further — so the
      // automatic answer is always at or below the inner ring's silhouette. That
      // is right when the gaps you are closing are LATERAL (sightlines down an
      // avenue), and useless when they are VERTICAL (sky over a low stretch of
      // the inner ring), which needs towers that rise above it.
      //
      // Clamping the knob before the cap would have meant typing a bigger number
      // and watching nothing happen, because the cap was already binding. So the
      // derivation is the safe default and the knob is absolute.
      const fitted = Math.min(matched, headroom / (size.y || 1));
      const s2 = fitted * qp('ring2h', 1);

      const pieceW2 = Math.max(1, (size.x || 1) * s2);
      const step2 = pieceW2 * 0.85;
      // Floor of six on the DERIVED count only — an explicit ?ring2n is an
      // instruction, and a knob that silently refuses the value you typed is
      // worse than no knob.
      const auto = Math.max(6, Math.ceil((2 * Math.PI * R2) / step2));
      const n2 = Math.max(3, Math.round(qp('ring2n', auto)));
      const groundY2 = -box.min.y * s2 - (size.y * s2 * SINK_FRAC);
      // Same inner-face solve the north run uses, in the piece's own frame: the
      // group rotation below carries it around the circle, so the trigonometry
      // only ever has to be right once.
      const zN2 = -R2 + ctr.z * s2 - (size.z * s2) / 2;

      for (let i = 0; i < n2; i++) {
        // Half a step off, so the outer seams do not line up with the inner
        // ring's — two seams on the same sightline is a hole through both.
        const phi = ((i + 0.5) / n2) * Math.PI * 2;
        outer.push({ obj: scene.clone(true), phi, pos: [0, groundY2, zN2], scale: s2 });
      }
      outerR = R2;
      // Apparent height as an ANGLE from the middle of the map, for both rings —
      // the only comparison that says whether the outer ring is hiding behind
      // the inner one (below 1.0) or standing over it (above). Heights in units
      // cannot answer that, because the rings are at different distances.
      const app2 = Math.atan2(size.y * s2, R2);
      const app1 = Math.atan2(TARGET_HEIGHT, R1);
      log2 = `${n2} pieces at R ${R2.toFixed(0)} · x${s2.toFixed(2)} → ${(size.y * s2).toFixed(0)}u tall`
        + ` · ${(pieceW2 / Math.max(1, 2 * R2 * Math.sin(Math.PI / n2))).toFixed(2)}x overlap`
        + ` · reads ${(app2 / app1).toFixed(2)}x the inner ring's height from the middle`
        + (fitted < matched - 1e-3 ? ` (headroom under the shell capped it from x${matched.toFixed(2)})` : '');
    }

    return { inner: out, outer, innerReach, outerR, note: `[skyline] inner ${out.length} pieces, reach ${innerReach.toFixed(0)} · outer ${log2}` };
  }, [scene, bounds]);

  // Tell the shell how much room the towers actually need.
  //
  // Reported from where the outer pieces STAND, not from how far their geometry
  // sprawls behind them. A chunk is deeper than the whole gap between the rings,
  // so measuring its far corner would push the shell out by hundreds of units to
  // contain bulk that sits behind its own front face — and the ground disc grows
  // with the shell, which would leave a wide band of empty ground visible OUTSIDE
  // the towers. That is the exact failure this ring exists to close.
  //
  // It also means the ring cannot literally stand ON the disc's rim: the shell is
  // defined as 1.25x the skyline's reach, so the furthest any tower can stand is
  // ~76% of the way out. That is as close to the horizon as the world's own
  // containment rule allows, and the rim behind it is hidden by the towers.
  useEffect(() => {
    if (!items.inner.length) return undefined;
    setSkylineOuter(Math.max(items.innerReach, items.outerR));
    return () => setSkylineOuter(0);
  }, [items, setSkylineOuter]);

  // One line per build, so the geometry above can be checked against what is
  // actually on screen rather than against my arithmetic.
  useEffect(() => { if (items.note) console.log(items.note); }, [items.note]);

  return (
    <group>
      {/* Ground filling the gap between the model city and the ring — extended
          WELL past the ring (1.9×) so its edge never shows on the horizon during
          the fly-in, and tinted with the time-of-day atmosphere so it reads as
          continuous haze-lit terrain instead of a black void.
          SUPPRESSED when the water is on (<SeaVista/> takes over this exact
          job, at this exact height): two flat surfaces both claiming the ground
          means the opaque one wins, and this one was hiding the sea. */}
      {/* The ground moved to CityBox: it now has to cover the whole enclosure
          (the towers stand out in that gap and would otherwise rise out of
          nothing), and a disc centred on the city is the wrong shape for a
          rectangular box. Kept here only as the pre-bounds fallback. */}
      {ground && !bounds && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.3, 0]}>
          <circleGeometry args={[RING_RADIUS * 1.9, 64]} />
          <meshBasicMaterial color={groundColor} toneMapped={false} fog={false} />
        </mesh>
      )}
      {items.inner.map((it, i) => (
        <primitive key={i} object={it.obj} position={it.pos} rotation={[0, it.rotY, 0]} scale={it.scale} />
      ))}
      {/* The outer ring, carried around the circle by a GROUP rotation rather
          than by trigonometry on each piece. Every one of these is the north run
          — same position, same facing — and the group turns it to its bearing,
          so the inner-face solve above only has to be right once instead of once
          per quadrant. That is the same reasoning the inner ring's 90° rotations
          rest on, generalised to any angle. */}
      {items.outer.map((it, i) => (
        <group key={`outer-${i}`} rotation={[0, it.phi, 0]}>
          <primitive object={it.obj} position={it.pos} rotation={[0, FACING, 0]} scale={it.scale} />
        </group>
      ))}
    </group>
  );
}

// BOTH builds now carry KTX2 textures, and useGLTF.preload() cannot parse those:
// the KTX2Loader needs a live WebGLRenderer to pick a GPU format, and there is
// no renderer at module scope. Preloading here threw
// "setKTX2Loader must be called before loading KTX2 textures" and took the whole
// Canvas down with it.
//
// A plain fetch gets the same head start without needing to decode anything —
// it warms the HTTP cache so the in-component load (which DOES have the loader,
// via useThree's gl) parses from memory instead of the network.
// The body is READ, not just requested: an unconsumed response is not reliably
// written to the HTTP cache, which would make the warm-up a no-op.
// Streamed and COUNTED (state/bootProgress): part of the door's percentage.
warmAndTrack('skyline', MODEL_URL);
